/**
 * Pending Photos Service
 * ----------------------
 * Gère les "photos en attente d'observation" pour le flux PWA mobile :
 * - prise de photo rapide (sans saisie immédiate d'observation)
 * - stockage IndexedDB (base64) pour fonctionner offline
 * - upload background vers l'API dès que le réseau est disponible
 * - listing / suppression depuis l'étape 11 (rattachement à une observation)
 *
 * Stockage : clé `mission_{missionId}_pending_photos` dans le store `inspections`
 * (cohabite avec les autres "steps" sans nécessiter de migration IndexedDB).
 *
 * Structure d'une pending photo :
 * {
 *   id: string (uuid local),
 *   blob_base64: string ("data:image/jpeg;base64,...") — la photo elle-même,
 *   url: string | null  — URL serveur une fois uploadée,
 *   upload_status: "pending" | "uploaded" | "failed",
 *   upload_error: string | null,
 *   context: {
 *     step_origin: string ("etape_9_armoires"),
 *     armoire_index?: number,
 *     armoire_designation?: string,
 *     circuit_index?: number,
 *     circuit_designation?: string,
 *     localisation: string,
 *     element: string,
 *     element_ref?: object,
 *   },
 *   created_at: string ISO,
 *   uploaded_at: string ISO | null,
 * }
 */

import inspectionStorage from './inspectionStorage';
import apiService from './apiService';

const STEP_KEY = 'pending_photos';

const genId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `pp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const safeGetList = async (missionId) => {
  if (!missionId) return [];
  try {
    const data = await inspectionStorage.getStep(missionId, STEP_KEY);
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.photos)) return data.photos;
    return [];
  } catch (err) {
    console.error('[pendingPhotos] getList error:', err);
    return [];
  }
};

const persist = async (missionId, photos) => {
  // On stocke directement le tableau via inspectionStorage.set (et NON saveStep) :
  // saveStep déclenche un auto-sync vers le backend, ce qui pousserait inutilement
  // les blobs base64. Les photos sont uploadées séparément via apiService.uploadFile.
  const key = `mission_${missionId}_${STEP_KEY}`;
  await inspectionStorage.set(key, photos);
};

/** Convertit un Blob/File en data URL base64 ("data:image/jpeg;base64,..."). */
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('FileReader error'));
    reader.readAsDataURL(file);
  });

/** Convertit une data URL base64 en Blob (pour ré-uploader). */
const dataUrlToBlob = (dataUrl) => {
  try {
    const [meta, b64] = String(dataUrl).split(',');
    const mime = (meta.match(/data:([^;]+);base64/) || [])[1] || 'image/jpeg';
    const binary = atob(b64);
    const len = binary.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) arr[i] = binary.charCodeAt(i);
    return new Blob([arr], { type: mime });
  } catch (err) {
    console.error('[pendingPhotos] dataUrlToBlob error:', err);
    return null;
  }
};

const pendingPhotosService = {
  /** Retourne la liste actuelle des photos en attente. */
  async list(missionId) {
    return await safeGetList(missionId);
  },

  /** Nombre de photos en attente. */
  async count(missionId) {
    const list = await safeGetList(missionId);
    return list.length;
  },

  /**
   * Ajoute une nouvelle pending photo.
   *  - lit le fichier en base64,
   *  - persiste immédiatement (offline-safe),
   *  - lance l'upload en background (best-effort).
   * @returns {Promise<object>} la photo créée (avec son id).
   */
  async add(missionId, file, context = {}) {
    if (!missionId) throw new Error('missionId requis');
    if (!file) throw new Error('Fichier requis');

    const base64 = await fileToBase64(file);
    const photo = {
      id: genId(),
      blob_base64: base64,
      url: null,
      upload_status: 'pending',
      upload_error: null,
      context: context || {},
      created_at: new Date().toISOString(),
      uploaded_at: null,
    };

    const list = await safeGetList(missionId);
    const updated = [...list, photo];
    await persist(missionId, updated);

    // Best-effort : upload background puis sync serveur. On ne bloque pas l'UI.
    this.uploadOne(missionId, photo.id)
      .finally(() => this.syncToServer(missionId))
      .catch((err) =>
        console.warn('[pendingPhotos] upload/sync background échoué :', err?.message || err),
      );

    return photo;
  },

  /** Récupère une pending photo par son id. */
  async get(missionId, photoId) {
    const list = await safeGetList(missionId);
    return list.find((p) => p.id === photoId) || null;
  },

  /** Supprime une pending photo (utilisé après rattachement à une observation ou rejet manuel). */
  async remove(missionId, photoId) {
    const list = await safeGetList(missionId);
    const next = list.filter((p) => p.id !== photoId);
    if (next.length === list.length) return false;
    await persist(missionId, next);
    this.syncToServer(missionId).catch(() => {});
    return true;
  },

  /** Met à jour une pending photo (utilisé en interne pour l'upload). */
  async update(missionId, photoId, patch) {
    const list = await safeGetList(missionId);
    const idx = list.findIndex((p) => p.id === photoId);
    if (idx === -1) return null;
    const updated = { ...list[idx], ...patch };
    const next = [...list];
    next[idx] = updated;
    await persist(missionId, next);
    return updated;
  },

  /**
   * Tente d'uploader UNE photo (pending → uploaded).
   * Sans réseau, ne fait rien (marque "failed" avec message). Retry possible plus tard.
   */
  async uploadOne(missionId, photoId) {
    const photo = await this.get(missionId, photoId);
    if (!photo) return null;
    if (photo.upload_status === 'uploaded' && photo.url) return photo;

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      await this.update(missionId, photoId, {
        upload_status: 'failed',
        upload_error: 'Hors-ligne',
      });
      return null;
    }

    const blob = dataUrlToBlob(photo.blob_base64);
    if (!blob) {
      await this.update(missionId, photoId, {
        upload_status: 'failed',
        upload_error: 'Conversion base64 → blob impossible',
      });
      return null;
    }

    const file = new File([blob], `pending_photo_${photoId}.jpg`, { type: blob.type || 'image/jpeg' });
    try {
      const result = await apiService.uploadFile(file, 'photo_observation');
      if (result?.success && result.file_url) {
        return await this.update(missionId, photoId, {
          url: result.file_url,
          upload_status: 'uploaded',
          uploaded_at: new Date().toISOString(),
          upload_error: null,
        });
      }
      await this.update(missionId, photoId, {
        upload_status: 'failed',
        upload_error: result?.error || 'Upload échoué (réponse vide)',
      });
      return null;
    } catch (err) {
      await this.update(missionId, photoId, {
        upload_status: 'failed',
        upload_error: err?.message || String(err),
      });
      return null;
    }
  },

  /**
   * Payload léger pour sync serveur (sans blob_base64).
   * Retente les uploads en échec avant de construire la liste.
   */
  async buildSyncPayload(missionId) {
    if (!missionId) return { photos: [], updated_at: new Date().toISOString() };
    try {
      await this.retryFailedUploads(missionId);
    } catch (err) {
      console.warn('[pendingPhotos] retryFailedUploads before sync:', err?.message || err);
    }
    const list = await safeGetList(missionId);
    const photos = list.map((p) => ({
      id: p.id,
      url: p.url || null,
      upload_status: p.upload_status || 'pending',
      upload_error: p.upload_error || null,
      context: p.context || {},
      created_at: p.created_at,
      uploaded_at: p.uploaded_at || null,
    }));
    return { photos, updated_at: new Date().toISOString() };
  },

  /** Pousse la liste pending vers le rapport serveur (best-effort, non bloquant). */
  async syncToServer(missionId) {
    if (!missionId || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      return { success: false, error: 'Hors ligne' };
    }
    try {
      const { default: syncManager } = await import('./syncManager');
      return await syncManager.syncPendingPhotos(missionId);
    } catch (err) {
      console.warn('[pendingPhotos] syncToServer échoué:', err?.message || err);
      return { success: false, error: err?.message || String(err) };
    }
  },

  /** Retente l'upload de toutes les pending photos non-uploadées (à appeler au retour online). */
  async retryFailedUploads(missionId) {
    const list = await safeGetList(missionId);
    const toRetry = list.filter((p) => p.upload_status !== 'uploaded' || !p.url);
    const results = await Promise.allSettled(
      toRetry.map((p) => this.uploadOne(missionId, p.id)),
    );
    return {
      attempted: toRetry.length,
      uploaded: results.filter((r) => r.status === 'fulfilled' && r.value).length,
    };
  },

  /** Vide la liste (réservé aux nettoyages : test, reset mission, etc.). */
  async clearAll(missionId) {
    await persist(missionId, []);
    this.syncToServer(missionId).catch(() => {});
  },
};

export default pendingPhotosService;
